// src/components/validationNotes/apiValidationNotes.js
// Appels de la direction vers /api/validation-notes (jeton employé).
//
// Les notes que les enseignants saisissent depuis leur espace arrivent ici par
// FEUILLE :
//   - collège et lycée : une classe × une matière × un trimestre × une épreuve
//     (0 « Évaluation de classe », 1 « Évaluation départementale »,
//     2 « Composition ») sur l'année active ;
//   - primaire (N3) : une classe × une matière × un MOIS (épreuve 0, trimestre
//     du mois), notée sur 10 au demi-point. La direction valide le mois de la
//     classe EN UN GESTE (toutes les matières soumises de ce mois) ; renvoi et
//     rectification restent par matière, sur la page de la feuille.
// La direction les lit, les valide — le serveur les reporte alors dans le
// carnet, sans jamais toucher une ligne verrouillée — ou les renvoie, et
// décide des demandes de rectification.
//
// C'est le jeton de l'ADMINISTRATION qui part ici (getAuthHeaders, clé
// `token`), jamais celui de l'espace enseignant (src/espace-enseignant/api.js).
//
// Formes rendues par le serveur (`data` de `{ success, data }`,
// backend2/services/NotesEnseignantService.js, contrat N3 §4) :
//
//   GET /?statut=a_traiter|tous|<statut>&niveau=primaire|secondaire&mois_id=&classe_id=
//        &enseignant_id=&trimestre=&epreuve=&page=&par_page=
//     → { droits: { secondaire: { voir, valider, rectifier }, primaire: { voir, valider, rectifier },
//                   deverrouiller, forcer },          // forme N1 tolérée : { voir, valider, rectifier, deverrouiller }
//         annee: { id, nom } | null,
//         compteurs: { brouillon, soumise, renvoyee, validee, rectification_demandee,
//                      rectification_autorisee, a_traiter },
//         feuilles: [ { id, classe_id, classe, matiere_id, matiere, trimestre, trimestre_libelle,
//                       epreuve, epreuve_libelle, statut, version, conflits, saisis,
//                       auteur: { id, nom } | null, soumise_par, soumise_le, valide_par, validee_le,
//                       demande_en_attente, mis_a_jour_le } ],
//         mois_primaire: [ { classe_id, classe, mois_id, mois, trimestre, enseignant, total_matieres,
//                            a_saisir, brouillons, soumises, renvoyees, validees, a_traiter } ],
//         page, pages, par_page, total,                // 50 par page par défaut, 200 au plus (`par_page`)
//         filtres: { classes: [{ id, nom }], enseignants: [{ id, nom }], mois?: [{ id, nom }] } }
//     Chaque niveau n'est montré qu'à qui en a la clé de lecture.
//
//   GET /primaire/:classeId/mois/:moisId                         (page du mois d'une classe du primaire)
//     → { classe, mois: { id, nom, trimestre, date_debut, date_fin, ouvert }, titulaire, eleves,
//         bareme: { max, pas },
//         matieres: [ { matiere_id, nom, coefficient, hors_moyenne,
//                       feuille: null | { id, statut, version, notes, absents, moyenne, auteur,
//                                         soumise_le, est_auteur },
//                       prevision: { inserees, remplacees, identiques, retirees,
//                                    conflits: [{ eleve_id, eleve, motif }] } } ],
//         totaux: { soumises, total, notes_a_reporter, conflits },
//         publication: { publie, lignes_verrouillees }, droits }
//     Les valeurs d'un brouillon ne sortent jamais du serveur.
//
//   POST /primaire/:classeId/mois/:moisId/valider { feuilles: [{ id, version }] }
//     → { validees: [ids], rapport: { inserees, mis_a_jour, identiques, retirees, conflits }, conflits: [...] }
//     Tout ou rien, dans une transaction : `VALIDATEUR_AUTEUR` (la matière est nommée) si l'employé a
//     saisi ou soumis l'une d'elles, `FEUILLE_MODIFIEE` si une version a changé.
//
//   GET /feuilles/:id
//     → { droits: { voir, valider, rectifier, deverrouiller, est_auteur },
//         actions: { valider, renvoyer, reporter, autoriser, refuser },   // ce que CET employé peut faire, ici
//         feuille: { id, classe_id, classe, section, matiere, coefficient, trimestre, trimestre_libelle,
//                    epreuve, epreuve_libelle, niveau?, mois?: { id, nom }, bareme?: { max, pas },
//                    annee: { id, nom, ouverte }, statut, version, brouillon,
//                    auteur, soumise_par, soumise_le, valide_par, validee_le, renvoyee_par, renvoyee_le,
//                    motif_renvoi, conflits, dernier_report_le },
//         compteurs: { eleves, saisis, ecarts, conflits_prevus, verrouillees, depublications },
//         eleves: [ { id, nom, prenom, dans_classe, valeur, absent, saisie,
//                     carnet: { note_id, valeur, positions, moyenne, verrouille, date_verrouillage,
//                               transferee } | null,
//                     lignes_mensuelles, reporte: { valeur, le } | null, ecart, trimestre_publie,
//                     depublie, prevision: { resultat, motif, explication } | null } ],
//         demandes: [ { id, statut, motif, eleves: [id], demandeur, demandee_le, decision_motif,
//                       decideur, decidee_le, notes_deverrouillees,
//                       pieces: [ { id, nom_fichier, type, taille, depose_le } ] } ],
//         journal: [ { id, demande_id, action, acteur_type, acteur: { id, nom }, resume, details, le } ] }
//
//   POST /feuilles/:id/valider    { version }          → la page relue + `rapport` { comptes, conflits, resultats }
//                                                         (feuille du primaire : 409 VALIDER_LE_MOIS)
//   POST /feuilles/:id/reporter   { version }          → idem (refait le report des élèves restés en conflit)
//   POST /feuilles/:id/renvoyer   { version, motif }   → la page relue
//   POST /rectification/:demandeId/autoriser { version, motif? } → la page relue + `deverrouillage`
//   POST /rectification/:demandeId/refuser   { version, motif }  → la page relue
//   GET  /pieces/:id                                   → le fichier, en téléchargement
//
// `version` est celle de la feuille que la personne a sous les yeux : le
// serveur refuse (409 FEUILLE_MODIFIEE) une décision prise sur un écran en
// retard — une feuille retirée et resoumise entre-temps n'est pas celle qu'on
// a lue.

import { getAuthHeaders } from '../../services/api';

const RACINE = '/api/validation-notes';

const LICENCE_FERMEE = "La validation des notes des enseignants n'est pas incluse dans la licence de cet établissement.";
const NON_INSTALLE = "La validation des notes des enseignants n'est pas encore installée dans cet établissement.";

// Même lecture des refus que les autres écrans de l'espace enseignant côté
// école (apiAvisEnseignants.js, apiSuiviEnseignants.js) : le refus de licence
// (`requireFeature`) n'a pas de `message` français, une route absente ou une
// clé pas encore créée (503 NOTES_VALIDATION_NON_INSTALLE) dit que le module
// n'est pas installé, le reste s'affiche tel que le serveur l'a écrit.
function erreurDuServeur(reponse, donnees, defaut) {
  let message = donnees?.message;
  if (!message && (donnees?.code === 'FEATURE_NOT_LICENSED' || reponse.status === 403)) message = LICENCE_FERMEE;
  if (!message && (reponse.status === 503 || (reponse.status === 404 && !donnees))) message = NON_INSTALLE;
  const erreur = new Error(message || donnees?.error || defaut);
  erreur.code = donnees?.code;
  erreur.statut = reponse.status;
  return erreur;
}

async function appeler(chemin, { methode = 'GET', corps } = {}) {
  let reponse;
  try {
    reponse = await fetch(`${RACINE}${chemin}`, {
      method: methode,
      headers: getAuthHeaders(),
      body: corps !== undefined ? JSON.stringify(corps) : undefined
    });
  } catch {
    throw new Error('Connexion au serveur impossible.');
  }
  let donnees = null;
  try { donnees = await reponse.json(); } catch { donnees = null; }
  if (!reponse.ok) throw erreurDuServeur(reponse, donnees, 'Une erreur est survenue.');
  return donnees;
}

/** Les filtres vides (« toutes les classes », « tous les enseignants ») ne partent pas dans l'adresse. */
const requete = (filtres = {}) => {
  const params = new URLSearchParams(
    Object.entries(filtres).filter(([, v]) => v !== undefined && v !== null && v !== '')
  );
  const texte = params.toString();
  return texte ? `?${texte}` : '';
};

/** Le nom proposé par le serveur (`Content-Disposition` de `res.download`), sinon un nom neutre. */
function nomDuFichier(reponse, defaut) {
  const entete = reponse.headers.get('Content-Disposition') || '';
  const encode = /filename\*=UTF-8''([^;]+)/i.exec(entete);
  if (encode) {
    try { return decodeURIComponent(encode[1]); } catch { /* nom illisible : on garde le défaut */ }
  }
  const simple = /filename="?([^";]+)"?/i.exec(entete);
  return simple ? simple[1] : defaut;
}

/**
 * Un justificatif, en blob. La pièce n'est servie que par l'API (le dossier
 * `uploads/notes-enseignants` répond 404 en accès direct) : le jeton voyage
 * dans l'en-tête, qu'un simple lien ne sait pas porter.
 */
async function lirePiece(pieceId) {
  const entetes = { ...getAuthHeaders() };
  delete entetes['Content-Type'];
  let reponse;
  try {
    reponse = await fetch(`${RACINE}/pieces/${encodeURIComponent(pieceId)}`, { headers: entetes });
  } catch {
    throw new Error('Connexion au serveur impossible.');
  }
  if (!reponse.ok) {
    let donnees = null;
    try { donnees = await reponse.json(); } catch { donnees = null; }
    throw erreurDuServeur(reponse, donnees, 'Justificatif introuvable.');
  }
  const blob = await reponse.blob();
  return { blob, type: blob.type || '', nom: nomDuFichier(reponse, `justificatif-${pieceId}`) };
}

const chemin = (...parties) => `/${parties.map((p) => encodeURIComponent(p)).join('/')}`;

export const validationNotes = {
  /**
   * La file : `{ statut, niveau, mois_id, classe_id, enseignant_id, trimestre, epreuve, page, par_page }`.
   * Rend aussi les mois des classes du primaire (`mois_primaire`) que la personne peut lire.
   */
  liste: (filtres) => appeler(requete(filtres)),
  /** La page d'une feuille : valeurs, carnet, prévision du report, journal, demandes. */
  feuille: (feuilleId) => appeler(chemin('feuilles', feuilleId)),
  /** Collège et lycée seulement : une feuille du primaire se valide avec son mois (`validerMois`). */
  valider: (feuilleId, version) => appeler(chemin('feuilles', feuilleId, 'valider'), {
    methode: 'POST',
    corps: { version }
  }),
  /** Refait le report des élèves restés en conflit (feuille validée, carnet déverrouillé depuis). */
  reporter: (feuilleId, version) => appeler(chemin('feuilles', feuilleId, 'reporter'), {
    methode: 'POST',
    corps: { version }
  }),
  renvoyer: (feuilleId, version, motif) => appeler(chemin('feuilles', feuilleId, 'renvoyer'), {
    methode: 'POST',
    corps: { version, motif }
  }),
  /** Le motif n'est exigé (10 caractères) que si des notes du carnet doivent être déverrouillées. */
  autoriserRectification: (demandeId, version, motif) => appeler(chemin('rectification', demandeId, 'autoriser'), {
    methode: 'POST',
    corps: { version, ...(motif ? { motif } : {}) }
  }),
  refuserRectification: (demandeId, version, motif) => appeler(chemin('rectification', demandeId, 'refuser'), {
    methode: 'POST',
    corps: { version, motif }
  }),
  /** La page du mois d'une classe du primaire : matières, états, prévision du report, publication. */
  mois: (classeId, moisId) => appeler(chemin('primaire', classeId, 'mois', moisId)),
  /**
   * Valide en un geste les matières soumises du mois : `feuilles` = `[{ id, version }]`,
   * les versions affichées (tout ou rien côté serveur).
   */
  validerMois: (classeId, moisId, feuilles) => appeler(chemin('primaire', classeId, 'mois', moisId, 'valider'), {
    methode: 'POST',
    corps: { feuilles }
  }),
  piece: lirePiece
};
